import axios from '@axios'
import Location from '@/mixins/classes/location'
import { useLocationOptions } from '@/views/apps/location/useLocation'

export default {
  namespaced: true,

  state: () => {
    const { initLocationData } = useLocationOptions()
    return {
      location: JSON.parse(JSON.stringify(initLocationData)),
      locations: [],
      loading: false,
    }
  },

  getters: {
    location: state => state.location,
    Location: state => new Location(state.location),
    locations: state => state.locations,
  },

  mutations: {
    setLoading(state, v) {
      state.loading = !!v
    },
    // ✅ Preserve existing address fields (like address_comment) unless explicitly overwritten
    setLocation(state, location) {
      if (location === undefined) return
      const { initLocationData } = useLocationOptions()

      const base = JSON.parse(JSON.stringify(initLocationData))
      const incoming = location || {}
      const current = state.location || base

      const incomingAddress =
          incoming.address && typeof incoming.address === 'object' ? incoming.address : {}
      const currentAddress =
          current.address && typeof current.address === 'object' ? current.address : {}

      const mergedAddress = {
        ...base.address,     // defaults
        ...currentAddress,   // preserve previously entered fields
        ...incomingAddress,  // apply new values if provided
      }

      state.location = {
        ...base,
        ...current,
        ...incoming,
        address: mergedAddress,
        exact: Object.prototype.hasOwnProperty.call(incoming, 'exact')
            ? incoming.exact
            : (current.exact ?? false),
      }
    },

    setLocationExact(state, value) {
      if (!state.location) {
        const { initLocationData } = useLocationOptions()
        state.location = JSON.parse(JSON.stringify(initLocationData))
      }
      state.location.exact = value
    },

    setLocationId(state, id) {
      state.location.id = id
    },

    setLocationPublicAlias(state, alias) {
      state.location.public_alias = alias
    },

    setLocationAddressLimited(state, limited) {
      state.location.address_limited = limited
    },

    deleteLocation(state) {
      const { initLocationData } = useLocationOptions()
      state.location = JSON.parse(JSON.stringify(initLocationData))
    },

    deleteLocations(state) {
      state.locations = []
    },

    setLocations(state, locations) {
      state.locations = locations
    },
  },

  actions: {
    async fetchLocations(ctx, queryParams) {
      ctx.commit('setLoading', true)
      try {
        const response = await axios.get('/addresses', { params: queryParams })
        if ([200, 201].includes(response?.status)) {
          ctx.commit('setLocations', response.data.data)
        }
        return response
      } catch (err) {
        // let caller / adapter handle
        throw err
      } finally {
        ctx.commit('setLoading', false)
      }
    },

    async fetchLocation(ctx, { id }) {
      try {
        const response = await axios.get(`/addresses/${id}`)
        if ([200, 201].includes(response?.status)) {
          ctx.commit('setLocation', response.data.data)
        }
        return response
      } catch (err) {
        if (err?.response?.status === 404) return err.response
        throw err
      }
    },

    async fetchPublicLocation(ctx, { id }) {
      try {
        const response = await axios.get(`/public/addresses/${id}`)
        if ([200, 201].includes(response?.status)) {
          ctx.commit('setLocation', response.data.data)
        }
        return response
      } catch (err) {
        if (err?.response?.status === 404) return err.response
        throw err
      }
    },

    async addLocation(ctx, location) {
      try {
        const response = await axios.post('/addresses', location)
        if ([200, 201].includes(response?.status)) {
          response.statusText = 'added'
          ctx.commit('setLocation', response.data.data)
        }
        return response
      } catch (err) {
        // important: rethrow to preserve axios shape (err.response.data.errors)
        throw err
      }
    },

    async addLocationLookup(ctx, location) {
      try {
        const response = await axios.post('/addresses?lookup', location)
        if ([200, 201].includes(response?.status)) {
          response.statusText = 'added'
          ctx.commit('setLocation', response.data.data)
          ctx.commit('setLocationId', response.data.data.id)
          ctx.commit('setLocationAddressLimited', response.data.data.address_limited)
          ctx.commit('setLocationPublicAlias', response.data.data.public_alias)
        }
        return response
      } catch (err) {
        throw err
      }
    },

    async updateLocation(ctx, location) {
      try {
        const response = await axios.patch(`/addresses/${location.id}`, location)
        if ([200, 201].includes(response?.status)) {
          ctx.commit('setLocation', response.data.data)
          response.statusText = 'updated'
        }
        return response
      } catch (err) {
        throw err
      }
    },

    async deleteLocation(ctx, { id }) {
      try {
        const response = await axios.delete(`/addresses/${id}`)
        if ([200, 201].includes(response?.status)) {
          response.statusText = 'deleted'
        }
        return response
      } catch (err) {
        throw err
      }
    },

    async refreshAndFocus({ state, commit, dispatch }, preferredId) {
      const args = { sort_by: 'created_at', order: 'desc', per_page: 200 }
      await dispatch('fetchLocations', args)

      const list = state.locations || []
      const byId = id => list.find(o => String(o.id) === String(id))

      let target = null
      if (preferredId != null) target = byId(preferredId)
      if (!target && state.location?.id) target = byId(state.location.id)
      if (!target && list.length) target = list[0]

      if (target) commit('setLocation', target)
      return target
    },

    async createAndFocus({ dispatch }, payload) {
      const resp = await dispatch('addLocation', payload) // will throw on validation -> caller catches
      const newId = resp?.data?.data?.id
      await dispatch('refreshAndFocus', newId)
      return resp?.data?.data
    },
  }

}
